import { useEffect } from "react";
import { useFitAllTruncated } from "@/components/ui/one-line";
import { WinCelebrations } from "@/components/engagement/WinCelebrations";
import { GetStartedGate } from "@/components/engagement/GetStartedGate";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { CopyFiOrb } from "@/components/CopyFiOrb";
import { ChatDock } from "@/components/community/ChatDock";
import { SpeedRunGuide } from "@/components/engagement/SpeedRunGuide";
import { SpeedRunBacktestWatch } from "@/components/engagement/SpeedRunBacktestWatch";
import { QuickLearnTour, QUICK_LEARN_EVENT } from "@/components/QuickLearnTour";
import { notePushOpen, startScreenTimer } from "@/lib/analytics";
import { NotificationBell } from "@/components/NotificationBell";
import { OpenTradesTray } from '@/components/OpenTradesTray';
import { AchievementsTray } from '@/components/engagement/AchievementsTray';
import { LogoOrb } from '@/components/LogoOrb';
import { LanguagePicker } from "@/components/layout/LanguagePicker";
import { TierLensSwitch, TierLensStripe } from "@/components/layout/TierLensSwitch";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import { ProfileDialog, openProfile } from "@/components/ProfileDialog";
import { TutorialVideoDialog, openTutorialVideo } from "@/components/TutorialVideoDialog";
import { IntentChooser } from "@/components/onboarding/IntentChooser";
import { CompetitionVideoDialog } from "@/components/competition/CompetitionVideoDialog";
import { CompetitionHeaderButton } from "@/components/competition/CompetitionHeaderButton";
import { IntentGuide } from "@/components/onboarding/IntentGuide";
import { useClaimPendingReferral } from "@/lib/referral";
import { PhemexReferralBanner } from "@/components/PhemexReferralBanner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { TradingSidebar } from "./TradingSidebar";
import { aiNavText } from "./aiNavText";
import { Button } from "@/components/ui/button";
import { SignOut, UserCircle, PlayCircle } from "@phosphor-icons/react";
import { intlLocale } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { getViewAsUserId } from "@/lib/viewAs";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/* The page a path belongs to, named as the menu names it: the header's title
   on every page but the dashboard, which shows the date (Guillaume, 2026-10-09). */
const PAGE_TITLES: [string, string][] = [
  ['/traders', 'nav.traders'],
  ['/configure', 'nav.strategies'],
  ['/copy-trade', 'nav.myCopies'],
  ['/sessions', 'nav.myCopies'],
  ['/assets', 'nav.assets'],
  ['/learn', 'nav.learn'],
  ['/ai', 'nav.aiConnections'],
  ['/community', 'nav.community'],
  ['/competition', 'nav.competition'],
  ['/admin', 'nav.activity'],
];
const pageTitleKey = (path: string): string | null =>
  PAGE_TITLES.find(([p]) => path === p || path.startsWith(p + '/'))?.[1] ?? null;

export function MainLayout() {
  // Every cut-with-"..." text shrinks first (Guillaume's one-line rule, 2026-10-21).
  useFitAllTruncated();
  /* Time on every screen, measured in one place.
     Per-page timers would have to be added to each new page and would be
     forgotten on the one that turns out to matter. The route IS the screen
     here, so the layout that renders every route is where this belongs. */
  const { pathname } = useLocation();
  useEffect(() => startScreenTimer(pathname), [pathname]);
  // A push tapped to get here (sw.js tags ?push=<kind>): notification_opened, once.
  useEffect(() => { notePushOpen(); }, [pathname]);

  const navigate = useNavigate();
  const { t, i18n } = useTranslation("common");
  const titleKey = pageTitleKey(pathname);
  // The AI section names itself from aiNavText.ts (Michika, 2026-10-22).
  const aiWords = aiNavText(i18n.language);
  const aiTitle = pathname.startsWith('/ai/mirofish') ? aiWords.mirofish : pathname.startsWith('/ai/hermes') ? aiWords.hermes : pathname === '/ai' || pathname.startsWith('/ai/') ? aiWords.ai : null;
  const { user, logout, isAdmin } = useAuth();
  // A referral code remembered from ?ref= or the sign-up field, attached once.
  useClaimPendingReferral(user?.id);

  /* Asked first, as on the phone: a log out does not stop running sessions,
     and that is worth saying before rather than wondering after. */
  const handleLogout = async () => {
    if (!window.confirm(`${t('mobile:settings.logout.title')}\n\n${t('mobile:settings.logout.body')}`)) return;
    try {
      await logout();
      toast.success(t('layout.loggedOut'));
    } finally {
      navigate('/auth');
    }
  };

  /*
   * Somebody else's account, said on every screen of it.
   *
   * This document identifies as another user to the API, so every number below
   * is theirs. A support view that looks identical to your own account is one
   * you eventually mistake for your own account -- and the moment you do, you
   * read their PnL as yours and act on it. The stripe is unmissable and cannot
   * be dismissed, because being sure is the entire feature.
   */
  const viewingAs = getViewAsUserId();

  return (
    <SidebarProvider>
      <div className="h-screen flex w-full bg-background overflow-hidden">
        {viewingAs && (
          <div
            className="fixed inset-x-0 top-0 z-[100] bg-warning text-warning-foreground
                       text-[11px] font-medium text-center py-1 px-3 pointer-events-none"
          >
            {t('layout.viewingAs', { user: viewingAs })}
          </div>
        )}
        {/* An admin looking through a tier's lens (TierLensSwitch). */}
        {!viewingAs && <TierLensStripe />}
        {/* The plans pop-up, opened by any lock (lib/upgrade.ts). */}
        <UpgradeDialog />
        {/* The Profile pop-up, from the account menu (components/ProfileDialog.tsx). */}
        <ProfileDialog />
        {/* The walkthrough video: once for a new account, then from the header (components/TutorialVideoDialog.tsx). */}
        <TutorialVideoDialog />
        {/* The welcome by intent (lib/onboarding.ts): what they came for, then a guide that does it. */}
        <IntentChooser />
        {/* A new account starts on Get Started (engagement redesign, 2026-10-21). */}
        <GetStartedGate />
        {/* A winning close, celebrated while the app is open (2026-10-21). */}
        <WinCelebrations />
        {/* No review request on the web since 2026-10-24 (Guillaume): it lives in the store
            apps, where ratings count (mobile-app ReviewPrompt). */}
        {/* The chooser's orange answer opens this (components/competition). */}
        <CompetitionVideoDialog />
        {/* Ambient background blobs for glass depth */}
        <div className="ambient-bg" />

        <TradingSidebar />

        <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden relative z-10">
          {/* Header — Glass */}
          <header className="sticky top-0 z-50 h-14 glass-heavy border-b border-white/[0.06]">
            <div className="flex h-full items-center justify-between gap-3 px-3 sm:px-6">
              {/* The menu toggle and the page's name on the one sticky row,
                  on every page (Guillaume, 2026-10-09). */}
              <div className="flex items-center gap-3 min-w-0">
                <SidebarTrigger className="text-foreground shrink-0" />
                <span className="text-sm font-semibold text-foreground truncate">
                  {pathname === '/'
                    ? new Intl.DateTimeFormat(intlLocale(), { weekday: 'long', month: 'long', day: 'numeric' }).format(new Date())
                    : aiTitle ?? (titleKey ? t(titleKey) : null)}
                </span>
                {/* The page's own controls, in the bar's empty stretch (HeaderActions, review #63). */}
                <div id="page-header-actions" className="hidden md:flex items-center gap-2 min-w-0 ms-2" />
              </div>

              <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                {/* The $1,000 competition, with its countdown, on every page. */}
                <ErrorBoundary fallback={null}>
                  <CompetitionHeaderButton />
                </ErrorBoundary>
                {/* Quick Learn and the tutorial video as icons only, like the phone (round 8 #211,
                    Guillaume 10-27): the header's only words are the date, the email and the
                    competition's time. The Orb sits centred in its own round button. */}
                <button
                  type="button"
                  data-tour="quick-learn"
                  onClick={() => window.dispatchEvent(new Event(QUICK_LEARN_EVENT))}
                  title={t('layout.quickLearn')}
                  aria-label={t('layout.quickLearn')}
                  className="relative overflow-hidden inline-flex items-center justify-center h-8 w-8 rounded-full border border-primary/30 bg-primary/10 hover:bg-primary/20 transition-colors"
                >
                  {/* Round 9 #300: the Orb a size up and its aura kept inside the button. */}
                  <LogoOrb className="h-6 w-6" still />
                </button>

                <button
                  type="button"
                  onClick={openTutorialVideo}
                  title={t('tutorialVideo.watch')}
                  aria-label={t('tutorialVideo.watch')}
                  className="hidden sm:inline-flex items-center justify-center h-8 w-8 rounded-full text-foreground border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
                >
                  <PlayCircle size={18} weight="fill" />
                </button>

                {/* Its own boundary. The app-level one wraps every route, so
                    an exception in the bell would take the whole page down with
                    it — and a notifications panel is never worth that. Failing
                    to nothing is the correct outcome here. */}
                {/* Achievements, a trophy beside the bell (Guillaume 10-25). */}
                <ErrorBoundary fallback={null}>
                  <AchievementsTray />
                </ErrorBoundary>

                <ErrorBoundary fallback={null}>
                  <NotificationBell />
                </ErrorBoundary>

                {/* Open trades, beside the bell. Its own boundary for the same
                    reason: live exposure is worth showing, never worth taking
                    the page down for. */}
                <ErrorBoundary fallback={null}>
                  <OpenTradesTray />
                </ErrorBoundary>

                {/* Language, last before the account (Guillaume, 2026-09-26:
                    "Quick Learn -> Notifications -> Trade trails -> Language"). */}
                <LanguagePicker compact />

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-2" title={t('layout.hint.account')}>
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-muted-foreground text-xs font-bold">
                          {user?.email?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <span className="hidden md:inline text-sm font-medium">{user?.email}</span>
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium">{user?.email}</p>
                        <p className="text-xs text-muted-foreground">
                          {t('layout.role', { role: isAdmin ? t('layout.roleAdmin') : t('layout.roleUser') })}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={openProfile} className="cursor-pointer">
                      <UserCircle size={16} className="me-2" />
                      {t('profile.open')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={openTutorialVideo} className="cursor-pointer">
                      <PlayCircle size={16} className="me-2" />
                      {t('tutorialVideo.watch')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {/* The phone's Settings heads these three rows the same way (common:legal.title). */}
                    <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">{t('legal.title')}</DropdownMenuLabel>
                    {/* The phone's Settings → "Legal and support", same words. */}
                    <DropdownMenuItem asChild className="cursor-pointer">
                      <a href="/privacy" target="_blank" rel="noopener">{t('legal.privacy')}</a>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="cursor-pointer">
                      <a href="/terms" target="_blank" rel="noopener">{t('legal.terms')}</a>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="cursor-pointer">
                      <a href="/support" target="_blank" rel="noopener">{t('legal.support')}</a>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer">
                      <SignOut size={16} className="me-2 rtl:-scale-x-100" />
                      {t('layout.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* The Admin badge is its own switch now: the tier lens.
                    Guillaume, 2026-09-26. Admins only. */}
                <TierLensSwitch />
              </div>
            </div>
          </header>

          {/* Register-with-our-link reminder. Renders nothing once a broker is connected. */}
          <PhemexReferralBanner />

          {/* Main Content */}
          <main className="flex-1 overflow-y-auto">
            {/* Per page, reset on navigation: a page that crashes keeps the
                menu around it, and the next page starts clean (M-75). */}
            <ErrorBoundary resetKey={pathname}>
              <Outlet />
            </ErrorBoundary>
          </main>

          {/* Floating CopyFi Orb */}
          <CopyFiOrb />

          {/* Private messages and group chats, docked at the bottom (components/community/ChatDock.tsx, 2026-10-24). */}
          <ChatDock />
          {/* The admin's speed run, step by step (lib/speedRunPath.core.ts). */}
          <SpeedRunGuide />
          {/* The run's backtest, watched wherever the person is (lib/backtestDoneWatch.core.ts). */}
          <SpeedRunBacktestWatch />

          {/* Quick Learn guided tour overlay */}
          <QuickLearnTour />

          {/* The welcome guides: build a strategy, copy a trader, connect an AI (components/onboarding). */}
          <IntentGuide />
        </div>
      </div>
    </SidebarProvider>
  );
}
